import { Button, ProgressBar } from "@bcc-ui/ui-components";
import React from "react";
import { useTranslation } from "react-i18next";

import CopyToClipboard from "components/copy-to-clipboard/CopyToClipboard";
import StartedGreyIcon from "components/icons/StartedGreyIcon";
import { LoadingIndicator } from "components/loading-indicator/LoadingIndicator";
import style from "components/reports/notification-details/notification-details.scss";
import { AssetExportJobDetail, AssetExportStatus } from "domain/assetExport";
import { Notification } from "domain/notification";
import { assetService } from "services/asset-management/AssetService";
import { formatTimestamp } from "utils/format";
import { useTheme } from "utils/useTheme";

import FailedRedNotificationIcon from "assets/images/icons/alert.svg";
import PassedGreenNotificationIcon from "assets/images/icons/checkMarkInCircle.svg";
import WarningYellowNotificationIcon from "assets/images/icons/quickIndeterminate.svg";

import testIds from "testIds.json";

const REFETCH_JOB_DETAILS_TIMER_MILLISECONDS = 5_000;

interface JobIdDto {
    job_uuid: string;
}

const AssetExportNotificationDetails = (props: {
    refreshCount: number;
    jobDetail?: Notification;
    setRefreshIcon: (show: boolean) => void;
}): React.JSX.Element => {
    const { t } = useTranslation();
    const { current: abortControllers } = React.useRef<AbortController[]>([]);
    const [jobDetails, setJobDetails] = React.useState<AssetExportJobDetail>();
    const [showInPercentage, setShowInPercentage] = React.useState<number>(0);
    const [loading, setLoading] = React.useState<boolean>();
    const [requestFailureMessage, setRequestFailureMessage] = React.useState<string>("");
    const theme = useTheme();

    const fetchData = () => {
        const abortController = new AbortController();
        abortControllers.push(abortController);

        let jobUuid: string;
        try {
            const jobIdDto: JobIdDto = JSON.parse(props.jobDetail?.data ?? "");
            jobUuid = jobIdDto.job_uuid;
        } catch {
            setLoading(false);
            return;
        }

        assetService
            .fetchAssetExportJob(jobUuid, abortController)
            .then((details: AssetExportJobDetail) => {
                setJobDetails(details);
                const hasProcessed = details.totalRequestedAssets !== null;
                const isFinished = details.status === "SUCCEEDED" || details.status === "WARNING";
                setShowInPercentage(
                    isFinished
                        ? 100
                        : hasProcessed && details.totalRequestedAssets > 0
                          ? Math.floor(
                                ((details.totalExportedAssets + details.totalFailedAssets) * 100) /
                                    details.totalRequestedAssets
                            )
                          : 0
                );
                props.setRefreshIcon(
                    details.status === "STARTED" &&
                        (details.totalExportedAssets < details.totalRequestedAssets || !hasProcessed)
                );
            })
            .catch(() => {
                if (!abortController.signal.aborted) {
                    props.setRefreshIcon(true);
                    setRequestFailureMessage(t("Notification.assetExportNotifications.requestFailed"));
                }
            })
            .finally(() => setLoading(false));
    };

    React.useEffect(() => {
        setLoading(true);
        fetchData();

        return () => {
            abortControllers.forEach((controller) => controller.abort());
        };
    }, [props.refreshCount]);

    React.useEffect(() => {
        if (jobDetails?.status === "STARTED") {
            const timer = setTimeout(() => {
                fetchData();
            }, REFETCH_JOB_DETAILS_TIMER_MILLISECONDS);
            return () => {
                clearTimeout(timer);
            };
        }
    });

    const totalAssetsExported = jobDetails?.totalExportedAssets ?? 0;
    const totalProcessed = (jobDetails?.totalExportedAssets ?? 0) + (jobDetails?.totalFailedAssets ?? 0);

    const createDetailsDialog = (jobStatus: AssetExportStatus | undefined) => {
        if (typeof jobDetails === "undefined" || typeof jobStatus === "undefined") {
            return null;
        }

        const detailsRecord: Record<AssetExportStatus, string> = {
            SUCCEEDED: t("Notification.assetExportNotifications.successDetails"),
            WARNING: t("Notification.assetExportNotifications.warningDetails", {
                totalAssetsExported,
                totalAssetsFailed: jobDetails.totalFailedAssets,
            }),
            FAILED: t("Notification.assetExportNotifications.failedDetails"),
            STARTED: t("Notification.assetExportNotifications.inProgressDetails", {
                totalAssets: jobDetails.totalRequestedAssets,
            }),
        };

        const iconRecord: Partial<Record<AssetExportStatus, string>> = {
            SUCCEEDED: PassedGreenNotificationIcon,
            WARNING: WarningYellowNotificationIcon,
            FAILED: FailedRedNotificationIcon,
        };

        const failedJobUuid = (
            <div>
                <strong>{t("Notification.assetExportNotifications.failedJobUuid")}</strong> {jobDetails.jobUuid}
                <CopyToClipboard value={jobDetails.jobUuid} />
            </div>
        );

        const statusDetails =
            jobStatus === "FAILED" ? (
                <div className={style.details}>
                    {detailsRecord[jobStatus]} {failedJobUuid}
                </div>
            ) : (
                <div className={style.details}>{detailsRecord[jobStatus]}</div>
            );

        const showImage =
            jobStatus === "STARTED" ? (
                <StartedGreyIcon key={1} pathColor={theme.contentBackgroundColor} fillColor={theme.textColor} />
            ) : (
                <img className={style.image} src={iconRecord[jobStatus]} alt={t("AltText.confirmationIcon")} />
            );

        const numberOfAssetsExported = (
            <div data-testid={testIds.workArea.asset.assetExportResultsDialog.numberOfExportAssetsLabel}>
                {jobDetails.totalRequestedAssets === null && jobStatus === "STARTED"
                    ? t("Notification.assetExportNotifications.fetchingInProgress")
                    : t("Notification.assetExportNotifications.numberOfExportAssets", {
                          totalProcessed,
                          totalAssets: jobDetails.totalRequestedAssets,
                      })}
            </div>
        );

        const isLinkAvailable =
            showInPercentage === 100 &&
            jobDetails.presignedUrl !== undefined &&
            jobDetails.linkExpirationDate !== undefined &&
            new Date(jobDetails.linkExpirationDate) >= new Date();

        const downloadSection = (
            <>
                <Button
                    variant="Primary"
                    onClick={() => {
                        if (jobDetails.presignedUrl) {
                            window.location.href = jobDetails.presignedUrl;
                        }
                    }}
                    disabled={!isLinkAvailable}
                >
                    {t("Notification.assetExportNotifications.downloadButton")}
                </Button>
                <div className={style.downloadField}>
                    {isLinkAvailable ? (
                        <>
                            <div>
                                {t("Common.downloadLink")}
                                <CopyToClipboard value={jobDetails.presignedUrl ?? ""} />
                            </div>
                            {t("Notification.reportNotifications.linkExpiration", {
                                date: formatTimestamp(jobDetails.linkExpirationDate ?? ""),
                            })}
                        </>
                    ) : (
                        t("Notification.reportNotifications.linkNotAvailable")
                    )}
                </div>
            </>
        );

        return (
            <>
                <div className={style.titleMessage}>{showImage}</div>
                {statusDetails}
                <div className={style.numberInPercent}>
                    {t("Notification.assetExportNotifications.showInPercentage", {
                        percentage: showInPercentage,
                    })}
                </div>
                {numberOfAssetsExported}
                <ProgressBar
                    progress={showInPercentage}
                    label=""
                    testId={testIds.workArea.asset.assetExportResultsDialog.statusInPercentageLabel}
                    className={style.progressBar}
                />
                {downloadSection}
            </>
        );
    };

    return (
        <div
            data-testid={testIds.workArea.asset.assetExportResultsDialog.itself}
            className={style.detailContainer}
            data-type={props.jobDetail?.type}
        >
            <>
                {loading ? (
                    <LoadingIndicator />
                ) : requestFailureMessage ? (
                    <div>{requestFailureMessage}</div>
                ) : (
                    createDetailsDialog(jobDetails?.status)
                )}
            </>
        </div>
    );
};

export default AssetExportNotificationDetails;
